import { If } from 'common/src/components';
import { findDOMNode } from 'react-dom';
import { $document } from '@umu/umu-dom';
import { Iconfont } from '@umu/umu-design';
import { commonLang, lang } from '@umu/ukit-lang';
import useDebounceFn from 'ahooks/es/useDebounceFn';
import './searchInputForContentBaseOfUseMicroPage.scss';
import { transformPx2Rem } from 'businessCommon/src/@utils';
import { IAppRecommendData, IAppSearchData } from '@umu/umu-types';
import React, { createRef, useState, useEffect, useRef, RefObject } from 'react';
import { SearchRange } from '@umu/umu-search/src/searchInputForContent/common/interface';
import { defaultRecommendData } from '@umu/umu-search/src/searchInputForContent/common/dataflow';
import FloatingLayerForContentBase from '@umu/umu-search/src/searchInputForContent/common/components/floatingLayerForContentBase/floatingLayerForContentBase';
import useUpdateEffect from 'ahooks/lib/useUpdateEffect';

export interface SearchInputForContentBaseOfUseMicroPageProps {
    isLogin?: boolean;
    isInIframe?: boolean;
    placeholder?: string;
    fontSize?: number;
    disabled?: boolean;
    searchKeyword?: string;
    searchRange: SearchRange;
    recommendData: IAppRecommendData;
    searchCallback?(keywords: string): void;
    cleanCallback(): void;
    searchInputOnFocus?(): void;
    clickCallback?(data: IAppSearchData, key: string): void;
    onChange?(keywords: string): void;
    onClearBtnClick?(): void;
    searchInputCallback?(keywords: string): void;
    searchInputClearCallback?: () => void;
    isEnterpriseAccount?: boolean;
    changeSearchRange?: (searchRange: SearchRange, keyword: string) => void;
}

const SearchInputForContentBaseOfUseMicroPage = (props: SearchInputForContentBaseOfUseMicroPageProps) => {
    const placeholder = props.placeholder || commonLang('搜索');
    const fontSize = props.fontSize || 14;
    const elementRef: RefObject<HTMLDivElement> = useRef(null);
    const recommendData = props.recommendData || defaultRecommendData;
    const { historySearch, popular, historyCourse, LenovoCourse } = recommendData;
    const [keywords, setKeywords] = useState(props.searchKeyword || '');
    const [panelType, setPanelType] = useState('history');
    const [showFloatingLayer, setShowFloatingLayer] = useState(false);
    const searchInputRef: RefObject<HTMLDivElement> = createRef();
    const searchInputKeywordRef = useRef<HTMLInputElement>(null);
    const [showMouseDown, setShowMouseDown] = useState(false);
    const [isCancel, setIsCancel] = useState(!!(keywords && keywords !== ''));
    const inputBoxTagRef = useRef<HTMLDivElement>(null);
    const searchIconRef = useRef<HTMLDivElement>(null);
    const isSearchResultRef = useRef(false);
    const isInputFocus = useRef(false);

    const setParentCallback = (value: string) => {
        setShowFloatingLayer(false);
        isSearchResultRef.current = true;

        if (props.searchCallback) {
            props.searchCallback(value);
        }
    };

    const setCancelKeyWord = () => {
        setKeywords('');
        setIsCancel(false);
        isSearchResultRef.current = false;
        props?.searchInputClearCallback?.();
    };

    const getRefInputValue = () => {
        const value = searchInputKeywordRef.current ? searchInputKeywordRef.current.value : '';
        return value;
    };

    const onChangePropDebounceWrap = useDebounceFn(props?.onChange || function () {}, {
        wait: 200,
        leading: false,
        trailing: true,
    });

    const onChange = () => {
        const value = getRefInputValue();
        setIsCancel(value !== '');
        setKeywords(value);
        onChangePropDebounceWrap?.run(value);

        if (value === '') {
            isSearchResultRef.current = false;
        }
    };

    const getSearchCourse = () => {
        const value = getRefInputValue();
        if (props.searchInputCallback) {
            props.searchInputCallback(value);
        }
    };

    const getKeyWorDuse = useDebounceFn(getSearchCourse, {
        wait: 500,
        leading: true,
        trailing: true,
    });

    const setSearchCourse = () => {
        const value = getRefInputValue();

        setKeywords(value);
        const types = value === '' ? 'history' : 'lenovo';
        if (!isSearchResultRef.current) {
            getKeyWorDuse.run();
            setShowFloatingLayer(isInputFocus.current);
            setPanelType(types);
        }
        return value;
    };

    const keyWordHook = useDebounceFn(setSearchCourse, {
        wait: 500,
        leading: false,
        trailing: true,
    });

    const onKeyUp = (e: React.KeyboardEvent) => {
        switch (e.keyCode) {
            case 13:
                setParentCallback(keywords);
                setShowFloatingLayer(false);
                isInputFocus.current = false;
                isSearchResultRef.current = true;
                searchInputKeywordRef.current?.blur();

                if (props.searchRange === SearchRange.wholeStation) {
                    setKeywords('');
                }

                break;
            default:
                panelChange();
                break;
        }
        keyWordHook.run();
    };

    const panelChange = () => {
        const value = getRefInputValue();
        const types = value === '' ? 'history' : 'lenovo';
        setPanelType(types);
        setIsCancel(value !== '');
        setShowFloatingLayer(isInputFocus.current);
    };

    const handleClick = (event: Event) => {
        if (elementRef && elementRef.current) {
            const target = event.target as HTMLElement;
            const divDom = elementRef.current && findDOMNode(elementRef.current);
            const res = divDom && divDom.contains(target);
            if (res) {
                panelChange();
                keyWordHook.run();
            }
            clickCallback({ mousedown: !!res, eventData: event }, 'mousedown');
        }
    };

    const searchInputOnFocus = () => {
        isInputFocus.current = true;
        setShowFloatingLayer(true);

        if (keywords) {
            setIsCancel(true);
        }

        clickCallback({ mousedown: true }, 'mousedown');
        getSearchCourse();

        if (keywords && keywords !== '') {
            setPanelType('lenovo');
        } else {
            setPanelType('history');
        }

        if (props.searchInputOnFocus) {
            props.searchInputOnFocus();
        }
    };

    const searchInputOnBlur = () => {
        if (!showMouseDown) {
            isInputFocus.current = false;
            setShowFloatingLayer(false);
        }

        setTimeout(() => {
            setIsCancel(false);
        }, 500);
    };

    const clickCallback = (data: IAppSearchData, key: string) => {
        if (key === 'mousedown') {
            setShowMouseDown(!!data.mousedown);
        }

        if (key === 'bubble' && data.keyword_id) {
            setKeywords(data.keyword || '');
        }

        if (props.clickCallback) {
            props.clickCallback(data, key);
        }
    };

    const getFloatShowOrHide = () => {
        return showFloatingLayer && isInputFocus.current;
    };

    const computedInputPaddingLeft = () => {
        const searchIconEl = searchIconRef.current;
        const inputBoxTagEl = inputBoxTagRef.current;
        const searchInputKeywordEl = searchInputKeywordRef.current;

        if (inputBoxTagEl && searchInputKeywordEl && searchIconEl) {
            searchIconEl.style.left = `calc(${inputBoxTagEl.offsetWidth}px - 4px)`;
            searchInputKeywordEl.style.paddingLeft = `calc(${inputBoxTagEl.offsetWidth}px + ${searchIconEl.offsetWidth}px + 2px)`;
            searchInputKeywordEl.style.width = `calc(100% - ${inputBoxTagEl.offsetWidth}px - ${searchIconEl.offsetWidth}px)`;
            return;
        }

        if (searchInputKeywordEl && searchIconEl) {
            searchIconEl.style.left = `12px`;
            searchInputKeywordEl.style.width = `calc(100% - 32px)`;
            searchInputKeywordEl.style.paddingLeft = `calc(9px + ${searchIconEl.offsetWidth}px + 9px)`;
        }
    };

    const changeSearchRange = (searchRange: SearchRange, keyword: string) => {
        props.changeSearchRange?.(searchRange, keyword);

        if (searchRange === SearchRange.wholeStation) {
            setKeywords('');
        }
    };

    const checkIsShowInputBoxTag = () => {
        if (props.isInIframe) {
            return props.searchRange === SearchRange.currentPage;
        }

        return props.searchRange === SearchRange.wholeStation;
    };

    useEffect(() => {
        computedInputPaddingLeft();
    }, [props.searchRange]);

    useUpdateEffect(() => {
        if (isInputFocus.current && !showMouseDown) {
            isInputFocus.current = false;
            setShowFloatingLayer(false);
        }
    }, [showMouseDown]);

    useEffect(() => {
        $document.addEventListener('mousedown', (e) => handleClick(e), true);
        return () => {
            $document.removeEventListener('mousedown', (e) => handleClick(e), true);
        };
    }, []);

    return (
        <div className="recommend-search-input search-input-for-content-base__use-micro-page" ref={searchInputRef}>
            <div className="search-input">
                <div className="input-box" ref={elementRef}>
                    <If condition={checkIsShowInputBoxTag()}>
                        <div ref={inputBoxTagRef} className="input-box-tag">
                            <div className="input-box-tag__container">
                                {props.isInIframe ? lang('页面内搜索') : lang('全站搜索')}
                            </div>
                        </div>
                    </If>
                    <div className="input-box-search-icon" ref={searchIconRef}>
                        <Iconfont type="search"></Iconfont>
                    </div>
                    <input
                        type="search"
                        disabled={props.disabled}
                        ref={searchInputKeywordRef}
                        onFocus={searchInputOnFocus}
                        onBlur={searchInputOnBlur}
                        className={`recommend-input-text search-input-keyword recommend-input`}
                        placeholder={placeholder}
                        style={{ fontSize: transformPx2Rem(`${fontSize}px`) }}
                        maxLength={100}
                        onChange={onChange}
                        onKeyUp={onKeyUp}
                        value={keywords}
                    />
                    <If condition={isCancel}>
                        <div className={`do-search-cancel`} onClick={() => setCancelKeyWord()} />
                    </If>
                    <If condition={getFloatShowOrHide()}>
                        <FloatingLayerForContentBase
                            inputType="useMicroPage"
                            className="search-panel"
                            panelType="pc"
                            isLogin={props.isLogin}
                            historySearch={historySearch}
                            cleanCallback={props.cleanCallback}
                            clickCallback={clickCallback}
                            popular={popular}
                            LenovoCourse={LenovoCourse}
                            historyCourse={historyCourse}
                            showType={panelType}
                            searchRange={props.searchRange}
                            keywords={keywords}
                            isInIframe={props.isInIframe}
                            changeSearchRange={changeSearchRange}
                            isEnterpriseAccount={props.isEnterpriseAccount}
                        />
                    </If>
                </div>
            </div>
        </div>
    );
};

export default SearchInputForContentBaseOfUseMicroPage;
